import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import * as React from 'react'
import { cn } from '@/lib/utils'

type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
  /** Render both vertical and horizontal scrollbars */
  dualAxis?: boolean
  /** Ref forwarded to the Radix viewport element (for scrollTo, etc.) */
  viewportRef?: React.ForwardedRef<HTMLDivElement>
  /** TanStack Router scroll restoration ID for this scroll container */
  scrollRestorationId?: string
}

function isIphoneBrowser(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iphone|ipod/i.test(navigator.userAgent)
}

function setForwardedRef<T>(ref: React.ForwardedRef<T> | undefined, value: T | null) {
  if (!ref) return
  if (typeof ref === 'function') {
    ref(value)
    return
  }
  ref.current = value
}

const ScrollArea = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaProps>(
  ({ className, children, dualAxis, viewportRef, scrollRestorationId, ...props }, ref) => {
    const setNativeScrollRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        setForwardedRef(ref, node)
        setForwardedRef(viewportRef, node)
      },
      [ref, viewportRef],
    )

    if (isIphoneBrowser()) {
      return (
        <div
          ref={setNativeScrollRef}
          className={cn(
            'relative rounded-[inherit] [-webkit-overflow-scrolling:touch]',
            dualAxis ? 'overflow-auto' : 'overflow-y-auto overflow-x-hidden',
            className,
          )}
          data-scroll-restoration-id={scrollRestorationId}
          {...props}
        >
          {children}
        </div>
      )
    }

    return (
      <ScrollAreaPrimitive.Root ref={ref} className={cn('relative overflow-hidden', className)} {...props}>
        <ScrollAreaPrimitive.Viewport
          ref={viewportRef}
          className="h-full w-full rounded-[inherit]"
          data-scroll-restoration-id={scrollRestorationId}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        {dualAxis && <ScrollBar orientation="horizontal" />}
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    )
  },
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none z-20 data-[state=visible]:animate-in data-[state=visible]:fade-in-0 data-[state=visible]:duration-150 data-[state=hidden]:animate-out data-[state=hidden]:fade-out-0 data-[state=hidden]:duration-300',
      orientation === 'vertical' && 'h-full w-3 p-[3px]',
      orientation === 'horizontal' && 'h-3 flex-col p-[3px]',
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-black/40" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
